import React, { useState } from 'react';
import {
  postTeamRequest,
  TeamRequestDuplicateError,
} from '../api/teamRequest';
import { TeamSearchContactMetadata } from '../types/auth/team';
import Spinner from './Spinner';

interface TeamNotListedPanelProps {
  contactMetadata: TeamSearchContactMetadata | null;
  suggestedTeamName?: string;
  userEmail?: string;
  showEmptyState?: boolean;
}

const TeamNotListedPanel: React.FC<TeamNotListedPanelProps> = ({
  contactMetadata,
  suggestedTeamName = '',
  userEmail = '',
  showEmptyState = false,
}) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [requesterName, setRequesterName] = useState(userEmail);
  const [requestedTeamName, setRequestedTeamName] = useState(suggestedTeamName);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  React.useEffect(() => {
    if (userEmail) {
      setRequesterName(userEmail);
    }
  }, [userEmail]);

  React.useEffect(() => {
    if (suggestedTeamName) {
      setRequestedTeamName(suggestedTeamName);
    }
  }, [suggestedTeamName]);

  React.useEffect(() => {
    if (showEmptyState) {
      setIsFormOpen(true);
    }
  }, [showEmptyState]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (!requesterName.trim()) {
      setError('Email is required.');
      return;
    }

    if (!requestedTeamName.trim()) {
      setError('Team name is required.');
      return;
    }

    if (!/\S+@\S+\.\S+/.test(requesterName.trim())) {
      setError('Please enter a valid email address.');
      return;
    }

    setIsSubmitting(true);
    try {
      const requesterEmail = requesterName.trim();
      const response = await postTeamRequest(
        {
          name: requesterEmail,
          requested_team_name: requestedTeamName.trim(),
          requester_email: requesterEmail,
        },
        contactMetadata?.request_team_path
      );
      setSuccessMessage(
        response?.message ||
          'Your request has been sent. Please return to register after Athlyte adds your team.'
      );
      setIsFormOpen(false);
    } catch (err: unknown) {
      if (err instanceof TeamRequestDuplicateError) {
        setError(err.message);
      } else {
        const message =
          (err as { response?: { data?: { message?: string; detail?: string } } })
            ?.response?.data?.message ||
          (err as { response?: { data?: { detail?: string } } })?.response?.data
            ?.detail ||
          (err as Error)?.message ||
          'Failed to send request. Please try again.';
        setError(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactEmail = contactMetadata?.contact_email;
  const notListedMessage = contactMetadata?.team_not_listed_message;

  return (
    <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-4">
      {showEmptyState && notListedMessage && (
        <p className="text-sm text-gray-700 mb-3">{notListedMessage}</p>
      )}

      {!showEmptyState && (
        <p className="text-sm text-gray-600 mb-3">
          {notListedMessage ||
            "Don't see your college? Search above, contact us, or request your team below."}
        </p>
      )}

      {contactEmail && (
        <p className="text-sm text-gray-600 mb-3">
          Questions?{' '}
          <a
            href={`mailto:${contactEmail}`}
            className="text-cyan-700 hover:text-cyan-900 underline font-medium"
          >
            Contact us at {contactEmail}
          </a>
        </p>
      )}

      {successMessage && (
        <div className="mb-3 p-3 bg-green-50 border border-green-200 text-green-800 text-sm rounded">
          {successMessage}
        </div>
      )}

      {error && (
        <div className="mb-3 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded">
          {error}
        </div>
      )}

      {!successMessage && (
        <>
          {!isFormOpen ? (
            <button
              type="button"
              onClick={() => setIsFormOpen(true)}
              className="text-sm text-cyan-700 hover:text-cyan-900 underline font-medium"
            >
              Request your team be added
            </button>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label
                  htmlFor="request-team-name"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Team / college name
                </label>
                <input
                  id="request-team-name"
                  type="text"
                  value={requestedTeamName}
                  onChange={(e) => setRequestedTeamName(e.target.value)}
                  placeholder="Enter your team/college name"
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>
              <div>
                <label
                  htmlFor="requester-email"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Your email
                </label>
                <input
                  id="requester-email"
                  type="email"
                  value={requesterName}
                  onChange={(e) => setRequesterName(e.target.value)}
                  placeholder="Enter your email address"
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center px-4 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:bg-cyan-300 text-white text-sm font-medium rounded-md"
                >
                  {isSubmitting ? (
                    <>
                      <Spinner size="xs" />
                      <span className="ml-2">Sending...</span>
                    </>
                  ) : (
                    'Submit request'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setIsFormOpen(false)}
                  className="text-sm text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </>
      )}
    </div>
  );
};

export default TeamNotListedPanel;
